// Pure aggregation of real synced data into the shape WeeklyDigestView / generate-weekly-digest
// expect. No Supabase or React imports here so it stays independently testable (see
// weeklyDigestData.test.ts, run via `deno test`).

export interface WeekProjectInput {
  id: string;
  name: string;
  health: 'on-track' | 'at-risk' | 'critical';
  progress: number;
  budget: number;
  spent: number;
}

export interface WeekTaskInput {
  status: string;
  project_external_id: string | null;
  created_at: string;
  updated_at: string;
  due_date: string | null;
}

export interface WeekMeetingInput {
  start_time: string;
  ai_action_items: unknown[] | null;
  ai_decisions: unknown[] | null;
}

export interface WeekProjectSummary {
  name: string;
  status: 'on-track' | 'at-risk' | 'delayed';
  progress: number;
  tasksCompleted: number;
  tasksTotal: number;
  budget: { spent: number; total: number };
}

export interface WeekData {
  dateRange: { start: string; end: string };
  projects: WeekProjectSummary[];
  tasks: { completed: number; newlyCreated: number; inProgress: number; blocked: number; overdue: number };
  meetings: { held: number; actionItemsGenerated: number; decisionsRecorded: number };
  emails: { processed: number; tasksExtracted: number; highPriority: number };
  team: { activeMembers: number; avgWorkload: number; topPerformer: string };
}

function mapHealthToStatus(health: WeekProjectInput['health']): WeekProjectSummary['status'] {
  return health === 'critical' ? 'delayed' : health;
}

function inWindow(iso: string, start: Date, end: Date): boolean {
  const t = new Date(iso).getTime();
  return t >= start.getTime() && t <= end.getTime();
}

export function buildWeekData(
  projects: WeekProjectInput[],
  tasks: WeekTaskInput[],
  meetings: WeekMeetingInput[],
  teamMemberIds: string[],
  weekStart: Date,
  weekEnd: Date,
  now: Date = new Date(),
): WeekData {
  const projectSummaries: WeekProjectSummary[] = projects.map((p) => {
    const projectTasks = tasks.filter((t) => t.project_external_id === p.id);
    return {
      name: p.name,
      status: mapHealthToStatus(p.health),
      progress: p.progress,
      tasksCompleted: projectTasks.filter((t) => t.status === 'done').length,
      tasksTotal: projectTasks.length,
      budget: { spent: p.spent, total: p.budget },
    };
  });

  const tasksSummary = {
    completed: tasks.filter((t) => t.status === 'done' && inWindow(t.updated_at, weekStart, weekEnd)).length,
    newlyCreated: tasks.filter((t) => inWindow(t.created_at, weekStart, weekEnd)).length,
    inProgress: tasks.filter((t) => t.status === 'in-progress').length,
    blocked: 0, // no "blocked" concept exists in synced task data today
    overdue: tasks.filter((t) => t.status !== 'done' && t.due_date && new Date(t.due_date).getTime() < now.getTime()).length,
  };

  const weekMeetings = meetings.filter((m) => inWindow(m.start_time, weekStart, weekEnd));
  const meetingsSummary = {
    held: weekMeetings.length,
    actionItemsGenerated: weekMeetings.reduce((sum, m) => sum + (m.ai_action_items?.length ?? 0), 0),
    decisionsRecorded: weekMeetings.reduce((sum, m) => sum + (m.ai_decisions?.length ?? 0), 0),
  };

  return {
    dateRange: {
      start: weekStart.toISOString().slice(0, 10),
      end: weekEnd.toISOString().slice(0, 10),
    },
    projects: projectSummaries,
    tasks: tasksSummary,
    meetings: meetingsSummary,
    // No synced source for inbox/email data yet (SmartInbox remains fully mock — Sprint L.2 scope).
    emails: { processed: 0, tasksExtracted: 0, highPriority: 0 },
    team: {
      activeMembers: new Set(teamMemberIds).size,
      // No real workload/performer signal exists yet — left honest rather than fabricated.
      avgWorkload: 0,
      topPerformer: '',
    },
  };
}
